import { type QueryObserverResult } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'

import { Banner } from 'slab'
import { useGetQuotingProspectsProspectIdCensus } from 'utils/web_api'
import { type QuotingEmployeeCensus, type QuotingPaginatedEmployeeCensusList } from 'utils/web_api.schemas'

import { useEmployer } from './EmployerProvider'

type CensusContextType = {
  census: QuotingEmployeeCensus | null;
  refetch: ReturnType<typeof useGetQuotingProspectsProspectIdCensus>['refetch'];
}

const CensusContext = createContext<CensusContextType>({
  census: null,
  refetch: async () => ({ data: null, isLoading: false, isError: false, error: null } as QueryObserverResult<unknown, unknown>),
})

export function useCensus () {
  return useContext(CensusContext)
}

function mostRecentCensus (censuses: QuotingPaginatedEmployeeCensusList | undefined) {
  if (!censuses?.data || censuses.data.length === 0) {
    return null
  }

  return (
    [...censuses.data].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0] || null
  )
}

export function CensusProvider () {
  const { employer } = useEmployer()

  const prospectId = employer?.quoting_prospect_id

  const {
    data: censuses,
    isLoading,
    error,
    refetch,
  } = useGetQuotingProspectsProspectIdCensus(prospectId ?? '', undefined, {
    query: {
      refetchOnMount: 'always',
      enabled: !!prospectId,
      refetchInterval: (query) => {
        const census = mostRecentCensus(query.state.data)
        if (['processed', 'failed'].includes(census?.status ?? '')) {
          return false
        }

        return 1000
      },
    },
  })

  // TODO: Handle pagination. Currently assumes all censuses are returned in
  // the first page.
  const currentCensus = useMemo(
    () => mostRecentCensus(censuses),
    [censuses?.data]
  )

  const value = useMemo(
    () => ({ census: currentCensus, refetch }),
    [currentCensus, refetch]
  )

  if (isLoading) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mt-20 w-full max-w-[45rem] mx-auto'>
        <Banner type='critical' title='Error loading census'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <CensusContext.Provider value={value}>
      <Outlet />
    </CensusContext.Provider>
  )
}
